import { EmptyState } from "@/components/CommandSystem";

const accentBar = { blue: "bg-blue-500", emerald: "bg-emerald-500", amber: "bg-amber-500", rose: "bg-rose-500", slate: "bg-slate-400" };

export function Field({ label, children, className = "" }) {
  return (
    <label className={`hx-account-field flex min-w-0 flex-col gap-1.5 ${className}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export function Card({ className = "", children }) {
  return <div className={`hx-account-card rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}>{children}</div>;
}

export function SectionTitle({ title, hint, right }) {
  return (
    <div className="hx-account-section-title flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>
        {hint && <p className="mt-0.5 text-sm text-slate-500">{hint}</p>}
      </div>
      {right}
    </div>
  );
}

export function MetricStrip({ metrics }) {
  return (
    <div className="hx-account-summary-strip grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {metrics.map((m) => (
        <Card key={m.label} className="relative overflow-hidden p-4 pl-5">
          <span className={`absolute inset-y-0 left-0 w-1 ${accentBar[m.tone] || accentBar.slate}`} />
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{m.label}</p>
          <p className="mt-1 font-display text-2xl font-semibold tabular-nums text-slate-900">{m.value}</p>
          {m.hint && <p className="mt-0.5 text-xs text-slate-400">{m.hint}</p>}
        </Card>
      ))}
    </div>
  );
}

export function MetricTile({ label, value, tone = "slate" }) {
  const toneClass = tone === "emerald" ? "text-emerald-600" : tone === "rose" ? "text-rose-600" : "text-slate-900";
  return (
    <Card className="hx-account-metric-tile p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 font-display text-2xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
    </Card>
  );
}

export function TabBar({ active, onChange, tabs }) {
  return (
    <div className="hx-account-tabbar flex flex-wrap gap-1.5 rounded-lg border border-slate-200 bg-white p-1.5 shadow-sm">
      {tabs.map(([key, label]) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={isActive}
            className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
              isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function DataTable({ columns, data, empty, getRowKey }) {
  const rowKey = (row, idx) => (getRowKey ? getRowKey(row, idx) : row.id ?? idx);
  if (!data || data.length === 0) {
    return (
      <Card className="hx-account-empty-table p-2">
        <EmptyState title={empty} description="No accounting records match this view yet." />
      </Card>
    );
  }
  return (
    <div className="min-w-0">
      <Card className="hx-account-table hidden overflow-hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {columns.map((c) => (
                  <th key={c.key} className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 ${c.align === "right" ? "text-right" : ""}`}>
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((row, idx) => (
                <tr key={rowKey(row, idx)} className="transition-colors hover:bg-blue-50/40">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 align-middle text-slate-700 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.cellClassName || ""}`}>
                      {c.render ? c.render(row, idx) : row[c.key] ?? "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-3 md:hidden">
        {data.map((row, idx) => (
          <Card key={rowKey(row, idx)} className="hx-account-mobile-row p-4">
            <dl className="grid gap-2.5">
              {columns.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-4">
                  <dt className="shrink-0 text-xs font-medium uppercase tracking-wide text-slate-400">{c.header || "-"}</dt>
                  <dd className={`min-w-0 text-right text-sm font-medium text-slate-800 ${c.align === "right" ? "tabular-nums" : ""}`}>
                    {c.render ? c.render(row, idx) : row[c.key] ?? "-"}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>
        ))}
      </div>
    </div>
  );
}
